import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import sessionBuilderService from '../services/sessionBuilderService';
import srsService from '../services/srsService';
import {
  PlayIcon,
  ClockIcon,
  AcademicCapIcon,
  FireIcon,
  StarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  UserIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const LearningPathDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState(null);
  const [sessionPreview, setSessionPreview] = useState(null);
  const [upcomingNodes, setUpcomingNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysLeft, setDaysLeft] = useState(null);
  const [subjectAccuracies, setSubjectAccuracies] = useState([]);
  const [timeVsAccuracy, setTimeVsAccuracy] = useState({ avgTime: 0, accuracy: 0 });
  const [sessionsPerWeek, setSessionsPerWeek] = useState(0);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const [resumeInfo, setResumeInfo] = useState(null); // {questionIndex, remainingSeconds}
  const [showWeekly, setShowWeekly] = useState(false);

  // lightweight analytics helper
  const track = (eventName, params = {}) => {
    try { console.log('analytics', eventName, params); } catch {}
  };

  const formatPercent = (value) => {
    const num = Number(value ?? 0);
    if (!isFinite(num)) return '0';
    return num.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      loadUserProgress();
      // load resume info from localStorage (saved by SessionInterface)
      try {
        const resume = JSON.parse(localStorage.getItem(`resume_${user.uid}`) || 'null');
        if (resume && resume.remainingSeconds > 0) {
          setResumeInfo(resume);
        }
      } catch {}
    }
  }, [user]);

  const loadUserProgress = async () => {
    try {
      setLoading(true);
      
      // Get user progress
      const progress = await sessionBuilderService.getUserProgress(user.uid);
      setUserProgress(progress);
      
      // Build session preview
      const session = await sessionBuilderService.buildSession(user.uid, progress);
      setSessionPreview(session);
      
      // Get upcoming nodes for timeline
      const upcoming = await getUpcomingNodes(progress);
      setUpcomingNodes(upcoming);

      // Compute dashboard metrics
      computeCountdown(progress);
      computeSubjectHeat(progress);
      await computeSessionsStats(user.uid, progress);
      
    } catch (error) {
      console.error('Error loading user progress:', error);
      toast.error('Failed to load progress');
    } finally {
      setLoading(false);
    }
  };
  const computeCountdown = (progress) => {
    try {
      const examTs = progress?.preferences?.examDate;
      const examDate = examTs?.toDate ? examTs.toDate() : (examTs ? new Date(examTs) : new Date('2026-04-09T00:00:00'));
      const now = new Date();
      const diffDays = Math.max(0, Math.ceil((examDate - now) / (1000 * 60 * 60 * 24)));
      setDaysLeft(diffDays);
    } catch (e) {
      setDaysLeft(null);
    }
  };

  const computeSubjectHeat = (progress) => {
    const acc = progress?.performance?.subjectAccuracy || {};
    const items = Object.keys(acc).map(k => ({ subject: k, accuracy: acc[k]?.accuracy || 0 }));
    setSubjectAccuracies(items);
    // time vs accuracy (overall)
    const total = progress?.questionHistory?.totalQuestionsAnswered || 0;
    const correct = progress?.questionHistory?.correctAnswers || 0;
    const avgTime = progress?.questionHistory?.averageResponseTime || 0;
    setTimeVsAccuracy({ avgTime, accuracy: total > 0 ? Math.round((correct / total) * 100) : 0 });
  };

  const computeSessionsStats = async (userId, progress) => {
    try {
      const { db } = await import('../firebase/config');
      const { collection, query, where, getDocs, Timestamp } = await import('firebase/firestore');
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const q = query(collection(db, 'sessions'), where('userId', '==', userId));
      const snap = await getDocs(q);
      const count = snap.docs.filter(d => {
        const createdAt = d.data().createdAt;
        const date = createdAt?.toDate ? createdAt.toDate() : (createdAt ? new Date(createdAt) : null);
        return date && date >= sevenDaysAgo;
      }).length;
      setSessionsPerWeek(count);
    } catch (e) {
      setSessionsPerWeek(0);
    }
  };

  const getUpcomingNodes = async (progress) => {
    try {
      // Get the next few learning nodes from the database
      const currentNodeId = progress?.currentPath?.currentNodeId || 'Mathematics_0_0_lesson';
      
      // For now, return mock data based on the current node (PCM-only)
      const mockUpcoming = [
        {
          id: 'next_lesson',
          type: 'lesson',
          title: 'Quadratic Equations – Factorization',
          subject: 'Mathematics',
          estimatedTime: 10,
          icon: 'lesson'
        },
        {
          id: 'practice_next',
          type: 'micro-practice',
          title: '10 MCQs – Kinematics',
          subject: 'Physics',
          estimatedTime: 4,
          icon: 'practice'
        },
        {
          id: 'review_session',
          type: 'review',
          title: 'SRS Reviews',
          subject: 'Mixed',
          estimatedTime: 3,
          icon: 'review'
        }
      ];
      
      return mockUpcoming;
    } catch (error) {
      console.error('Error getting upcoming nodes:', error);
      return [];
    }
  };

  const handleStartSession = async () => {
    try {
      if (!sessionPreview) {
        toast.error('No session available');
        return;
      }

      track('today_plan_start');
      // Navigate to session interface
      navigate('/session', { 
        state: { 
          session: sessionPreview 
        } 
      });
      
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start session');
    }
  };

  const getNodeIcon = (type) => {
    switch (type) {
      case 'lesson':
        return <AcademicCapIcon className="h-5 w-5 text-blue-600" />;
      case 'micro-practice':
        return <PlayIcon className="h-5 w-5 text-green-600" />;
      case 'microPractice':
        return <PlayIcon className="h-5 w-5 text-green-600" />;
      case 'review':
        return <ArrowRightIcon className="h-5 w-5 text-purple-600" />;
      case 'checkpoint':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />;
      default:
        return <AcademicCapIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getSubjectColor = (subject) => {
    switch (subject) {
      case 'Mathematics':
        return 'bg-blue-100 text-blue-800';
      case 'Physics':
        return 'bg-purple-100 text-purple-800';
      case 'Chemistry':
        return 'bg-green-100 text-green-800';
      case 'mixed':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Derive Today's Plan summary from sessionPreview
  const todaysPlan = (() => {
    const nodes = sessionPreview?.nodes || [];
    const typeOf = (n) => n.nodeType || n.type;
    const reviews = nodes.filter(n => typeOf(n) === 'review').length;
    const lessons = nodes.filter(n => typeOf(n) === 'lesson').length;
    const practice = nodes.filter(n => typeOf(n) === 'micro-practice' || typeOf(n) === 'microPractice').length;
    const miniMocks = nodes.filter(n => typeOf(n) === 'checkpoint' || typeOf(n) === 'mini-mock').length;
    const tasks = [reviews, lessons, practice, miniMocks].reduce((a,b)=>a+(b>0?1:0),0) || 0;
    const estMin = sessionPreview?.estimatedDuration || (reviews*4 + lessons*6 + practice*6 + miniMocks*8) || 12;
    const done = 0;
    return { reviews, lessons, practice, miniMocks, tasks, estMin, done };
  })();

  const streakDays = userProgress?.streak || 0;
  const streakUnit = streakDays === 1 ? 'day' : 'days';
  const totalSessions = userProgress?.sessionStats?.totalSessions || 0;
  const totalXPVal = userProgress?.totalXP || 0;
  const avgSessionMin = userProgress?.sessionStats?.averageSessionLength || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your learning path...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">W3Skills</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-3 text-xs sm:text-sm text-gray-700">
                <div><span className="font-medium">{daysLeft !== null ? `${daysLeft} days` : 'Set exam date'}</span></div>
                <span>•</span>
                <div><span>Streak: </span><span className="font-medium">{(userProgress?.streak ?? 0) > 0 ? `${userProgress.streak}d` : '—'}</span></div>
                <span>•</span>
                <div><span>Today: </span><span className="font-medium">{todaysPlan.done}/{todaysPlan.tasks}</span></div>
              </div>
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setIsProfileMenuOpen((v) => !v)}
                  className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  title="Profile menu"
                >
                  <UserIcon className="h-5 w-5 mr-2" />
                  {user?.displayName ? user.displayName.split(' ')[0] : 'Profile'}
                  <svg className="ml-2 h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.127l3.71-3.896a.75.75 0 111.08 1.04l-4.24 4.46a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>

                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border rounded-md shadow-lg z-50">
                    <button
                      onClick={() => { setIsProfileMenuOpen(false); navigate('/profile'); }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <UserIcon className="h-5 w-5 mr-2" />
                      Profile
                    </button>
                    <button
                      onClick={async () => { setIsProfileMenuOpen(false); await logout(); navigate('/login'); }}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-gray-50 flex items-center"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                Welcome back, {user?.displayName || 'Student'}! 👋
              </h2>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                Ready to continue your learning journey? Your personalized path is waiting.
              </p>
            </div>
            <div className="hidden sm:block text-right">
              <div className="text-sm text-gray-600">Path Progress</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatPercent(userProgress?.currentPath?.pathProgress)}%
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 sm:h-12 sm:w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="text-right">
                <div className="text-[11px] sm:text-sm text-gray-600">Total Sessions</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{totalSessions}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 sm:h-12 sm:w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FireIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <div className="text-right">
                <div className="text-[11px] sm:text-sm text-gray-600">Streak</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{streakDays} {streakUnit}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 sm:h-12 sm:w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <StarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
              </div>
              <div className="text-right">
                <div className="text-[11px] sm:text-sm text-gray-600">Total XP</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{totalXPVal}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 sm:h-12 sm:w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
              </div>
              <div className="text-right">
                <div className="text-[11px] sm:text-sm text-gray-600">Avg Session</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{avgSessionMin} min</div>
              </div>
            </div>
          </div>
        </div>

        {/* Section A – Today’s Plan (hero) */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative inline-flex items-center justify-center">
                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-100" style={{ background: `conic-gradient(#2563EB ${todaysPlan.tasks? (todaysPlan.done/todaysPlan.tasks)*360 : 0}deg, #E5E7EB 0deg)` }}></div>
                  <div className="absolute text-[9px] sm:text-[10px] text-gray-700">{todaysPlan.done}/{todaysPlan.tasks}</div>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">Today’s Plan ({todaysPlan.estMin} min • {todaysPlan.tasks} tasks)</h3>
                <button title="Chosen from your accuracy & time last 7 days." className="text-gray-400 hover:text-gray-600 text-xs">i</button>
              </div>
              <button onClick={() => setShowWeekly(true)} className="hidden sm:block text-xs sm:text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 whitespace-nowrap">Weekly: 2 chapter tests + 1 mini-mock</button>
            </div>
            <button onClick={() => setShowWeekly(true)} className="sm:hidden self-start text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Weekly: 2 chapter tests + 1 mini-mock</button>
          </div>
          <ul className="mt-3 space-y-3">
            <li className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                <span>Reviews (SRS): <span className="font-semibold">{todaysPlan.reviews} questions</span> • due now</span>
              </div>
              <span className="hidden sm:block text-sm text-gray-500">~4 min</span>
            </li>
            <li className="flex items-center justify-between">
              <div className="flex items-center">
                <AcademicCapIcon className="h-5 w-5 text-green-600 mr-2" />
                <span>New: <span className="font-semibold">Quadratic Equations – Factorization</span></span>
              </div>
              <span className="hidden sm:block text-sm text-gray-500">~6 min</span>
            </li>
            <li className="flex items-center justify-between">
              <div className="flex items-center">
                <PlayIcon className="h-5 w-5 text-purple-600 mr-2" />
                <span>Timed Practice: <span className="font-semibold">10 MCQs – Kinematics</span></span>
              </div>
              <span className="hidden sm:block text-sm text-gray-500">~6 min</span>
            </li>
            <li className="flex items-center justify-between">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mr-2" />
                <span>Mini-Mock: <span className="font-semibold">10Q Mixed PCM</span></span>
              </div>
              <span className="hidden sm:block text-sm text-gray-500">~8 min</span>
            </li>
          </ul>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              onClick={handleStartSession}
              className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-3 bg-gradient-to-r from-blue-600 to-orange-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-orange-600 transition-colors"
            >
              <PlayIcon className="h-5 w-5 mr-2" /> Start Today’s Plan
            </button>
            <div className="mt-1 sm:mt-0 text-sm text-gray-600">Finish today to keep your streak alive.</div>
          </div>
        </div>

        {/* Resume strip (if session in progress) */}
        {resumeInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-blue-900">
              Paused at <span className="font-semibold">Q{resumeInfo.questionIndex + 1}</span> / {resumeInfo.totalQuestions} • <span className="font-semibold">{Math.floor(resumeInfo.remainingSeconds/60)}:{String(resumeInfo.remainingSeconds%60).padStart(2,'0')}</span> remaining
              <span className="ml-2 text-blue-700/80">(streak unaffected if you resume within 2h)</span>
            </div>
            <button
              onClick={() => { track('resume_session_click'); navigate('/session', { state: { session: sessionPreview, resume: true } }); }}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Resume
            </button>
          </div>
        )}

        {/* Section B – Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <button onClick={() => { track('quick_action_click', { action: 'practice' }); navigate('/practice'); }} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">Practice</div>
            <div className="text-sm text-gray-600">CET-level questions</div>
          </button>
          <button onClick={() => { track('quick_action_click', { action: 'mock' }); navigate('/mock-tests'); }} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">Mock Tests</div>
            <div className="text-sm text-gray-600">Full/sectional</div>
          </button>
          <button onClick={() => { track('quick_action_click', { action: 'score_predictor' }); navigate('/cet-score-predictor'); }} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">Score Predictor</div>
            <div className="text-sm text-gray-600">Progress-aware</div>
          </button>
          <button onClick={() => { track('quick_action_click', { action: 'college_predictor' }); navigate('/college-predictor'); }} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">College Predictor</div>
            <div className="text-sm text-gray-600">PCM-only</div>
          </button>
        </div>

        {/* Session Length Selector - Removed since we use user preferences */}
        {/* <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Length</h3>
          <div className="flex space-x-4">
            {[10, 16, 25].map((length) => (
              <button
                key={length}
                onClick={() => setSessionLength(length)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  sessionLength === length
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {length} min
              </button>
            ))}
          </div>
        </div> */}

        {/* Removed placeholder timeline card */}

        {/* Section C – Insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Subject Readiness</h3>
            <div className="space-y-3">
              {subjectAccuracies.length === 0 && (
                <div className="text-gray-500 text-sm">You’ll see strengths/weaknesses after your first session.</div>
              )}
              {subjectAccuracies.map(item => {
                const pct = Math.round((item.accuracy || 0) * 100);
                const readiness = pct >= 80 ? 'A' : pct >= 60 ? 'B' : 'C';
                const bar = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
                return (
                  <div key={item.subject} className="flex items-center">
                    <div className="w-32 text-gray-700">{item.subject}</div>
                    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-3 ${bar}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <div className="w-10 text-right text-gray-700 ml-3 text-sm">{readiness}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Time vs Accuracy</h3>
            {timeVsAccuracy.accuracy === 0 ? (
              <div className="text-gray-500 text-sm">Appears after ≥10 answers.</div>
            ) : (
              <>
                <div className="text-gray-700">Average Response Time: <span className="font-semibold">{Math.round(timeVsAccuracy.avgTime)}s</span></div>
                <div className="text-gray-700 mt-2">Accuracy: <span className="font-semibold">{timeVsAccuracy.accuracy}%</span></div>
                <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-2 bg-blue-600" style={{ width: `${Math.min(100, timeVsAccuracy.accuracy)}%` }}></div>
                </div>
                <p className="mt-2 text-sm text-gray-600">You answer <span className="font-semibold">{timeVsAccuracy.avgTime <= 7 ? 'fast' : 'slow'}</span> but <span className="font-semibold">{timeVsAccuracy.accuracy >= 70 ? 'accurate' : 'inaccurate'}</span> — {timeVsAccuracy.accuracy < 70 ? 'slow down by ~3s/Q.' : 'keep it up.'}</p>
              </>
            )}
          </div>
        </div>

        {/* Section D – Activity & Milestones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Activity</h3>
            <p className="text-gray-700">Past 7 days: <span className="font-semibold">{sessionsPerWeek}</span> sessions</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Milestones</h3>
            <p className="text-gray-700">Next milestone: <span className="font-semibold">Complete 5 days streak</span> → unlock ‘Consistency’ badge</p>
          </div>
        </div>

        {/* Section E – Announcements / Coach tips */}
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Coach tip</h3>
              <p className="text-gray-700">New: Chapter-wise CET PYQs (2019–2024) in Math</p>
            </div>
            <button className="text-sm text-gray-500 hover:text-gray-700" onClick={(e)=>{e.currentTarget.closest('div.bg-white').remove();}}>Dismiss</button>
          </div>
        </div>
      </div>
      {/* Weekly schedule modal */}
      {showWeekly && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={()=>setShowWeekly(false)}>
          <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-md" onClick={(e)=>e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Weekly Plan</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>Mon: Reviews + New concept</li>
              <li>Tue: Timed practice (Physics)</li>
              <li>Wed: Timed practice (Chemistry)</li>
              <li>Thu: Reviews + recap</li>
              <li>Fri: Chapter test</li>
              <li>Sat: Reviews</li>
              <li>Sun: Mini‑mock</li>
            </ul>
            <div className="mt-4 text-right">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md" onClick={()=>setShowWeekly(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Sticky Start button on mobile */}
      <div className="fixed bottom-0 left-0 right-0 sm:hidden bg-white/95 border-t p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-2">
          <button onClick={handleStartSession} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold shadow">
            Start Today’s Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearningPathDashboard;
